<template>
  <div class="breadcrumb-container" v-if="breadcrumbs.length > 0">
    <div class="header-bg"></div>
    <div class="container">
      <template v-for="(item, index) in breadcrumbs">
        <span :key="index" v-if="item.name">
          <span v-if="index !== 0" class="separator"> > </span>
          <router-link v-if="item.path" :to="item.path">{{ item.name }}</router-link>
          <span v-else class="current">{{ item.name }}</span>
        </span>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'Breadcrumb',
  data() {
    return {
      breadcrumbs: [],
      routeHistory: [] // 存储最近访问的路由历史
    };
  },
  watch: {
    '$route'(to, from) {
      // 路由变化时，将上一个路由添加到历史记录
      if (from && from.path) {
        this.addRouteToHistory(from);
      }
      // 然后生成面包屑
      this.generateBreadcrumbs();
    }
  },
  created() {
    this.loadRouteHistory();
    // 初始化时直接生成面包屑，不将当前路由添加到历史记录
    // 因为当前路由会在面包屑中显示，不应该出现在历史记录中
    this.generateBreadcrumbs();
  },
  methods: {
    // 加载路由历史
    loadRouteHistory() {
      try {
        const saved = sessionStorage.getItem('breadcrumb_history');
        if (saved) {
          this.routeHistory = JSON.parse(saved);
        }
      } catch (e) {
        console.error('加载路由历史失败', e);
        this.routeHistory = [];
      }
    },
    // 保存路由历史
    saveRouteHistory() {
      try {
        sessionStorage.setItem('breadcrumb_history', JSON.stringify(this.routeHistory));
      } catch (e) {
        console.error('保存路由历史失败', e);
      }
    },
    // 判断是否为首页
    isHomeRoute(route) {
      const path = route.path || route;
      // 移除语言前缀后判断
      const pathWithoutLang = path.replace(/^\/(zh|en|ja|ko|ru|es|fr|ar)/, '');
      return pathWithoutLang === '/' || pathWithoutLang === '';
    },
    // 添加路由到历史记录
    addRouteToHistory(route) {
      const routeInfo = {
        path: route.path,
        name: this.getRouteName(route),
        fullPath: route.fullPath,
        timestamp: Date.now() // 添加时间戳用于排序
      };

      // 如果是首页，不添加到历史记录
      if (this.isHomeRoute(routeInfo.path)) {
        return;
      }

      // 如果路由没有名称，不添加
      if (!routeInfo.name) {
        return;
      }

      // 移除相同的路由（如果已存在），避免重复
      // 这样如果用户返回到之前访问过的路由，它会重新出现在历史记录的末尾
      this.routeHistory = this.routeHistory.filter(
        item => item.fullPath !== routeInfo.fullPath
      );

      // 添加到历史记录末尾（按访问时间顺序：最早的在前面，最新的在后面）
      this.routeHistory.push(routeInfo);

      // 只保留最近3个路由（取最后3个，即最新的3个）
      // 这样历史记录中始终是最新的3个路由，按访问顺序排列
      if (this.routeHistory.length > 3) {
        this.routeHistory = this.routeHistory.slice(-3);
      }

      this.saveRouteHistory();
    },
    // 获取路由名称
    getRouteName(route) {
      const matched = route.matched.find(item => item.meta && item.meta.breadcrumb);
      if (!matched) {
        return '';
      }

      const breadcrumb = matched.meta.breadcrumb;
      let name = typeof breadcrumb === 'function' ? breadcrumb(route) : breadcrumb.name;

      // Support for i18n
      if (this.$t && name) {
        name = this.$t(name);
      }

      // Handle dynamic segments
      if (route.params.id && matched.path.includes(':id')) {
        if (route.meta.dynamicBreadcrumb) {
          name = route.meta.dynamicBreadcrumb(route) || name;
        } else {
          // Fallback for product name if not provided dynamically
          name = route.params.name || this.productName || name;
        }
      }

      return name;
    },
    // 生成面包屑
    generateBreadcrumbs() {
      const breadcrumbs = [];
      const currentPath = this.$route.path;
      const isHome = this.isHomeRoute(currentPath);

      // 如果不在首页，第一个添加首页
      if (!isHome) {
        // 获取首页名称，支持i18n
        let homeName = '首页';
        if (this.$t) {
          // 尝试翻译，如果翻译失败则使用原值
          try {
            homeName = this.$t('首页') || '首页';
          } catch (e) {
            homeName = '首页';
          }
        }
        breadcrumbs.push({
          name: homeName,
          path: this.getHomePath()
        });
      }

      // 添加最近访问的3个路由（不包括当前路由和首页）
      // 从历史记录中筛选，排除当前路由和首页，按访问时间顺序显示（最早的在前）
      const recentRoutes = this.routeHistory
        .filter(item => {
          // 排除当前路由
          if (item.fullPath === this.$route.fullPath) {
            return false;
          }
          // 排除首页
          if (this.isHomeRoute(item.path)) {
            return false;
          }
          return true;
        })
        .slice(-3); // 取最后3个（即最新的3个），但保持时间顺序

      // 按时间顺序添加到面包屑（最早的在前，最新的在后）
      recentRoutes.forEach(route => {
        breadcrumbs.push({
          name: route.name,
          path: route.path
        });
      });

      // 添加当前路由（如果不在首页）
      if (!isHome) {
        const currentName = this.getRouteName(this.$route);
        if (currentName) {
          breadcrumbs.push({
            name: currentName,
            path: null // 当前页面不显示链接
          });
        }
      }

      this.breadcrumbs = breadcrumbs;
    },
    // 获取首页路径（考虑语言前缀）
    getHomePath() {
      const currentLang = localStorage.getItem('language') || 'en';
      const pathWithoutLang = this.$route.path.replace(/^\/(zh|en|ja|ko|ru|es|fr|ar)/, '');
      
      // 如果当前路径有语言前缀，首页也加上语言前缀
      if (this.$route.path.match(/^\/(zh|en|ja|ko|ru|es|fr|ar)/)) {
        return `/${currentLang}/`;
      }
      return '/';
    },
    fillParams(path, params) {
      for (const key in params) {
        path = path.replace(`:${key}`, params[key]);
      }
      return path;
    }
  },
  props: {
    productName: {
      type: String,
      default: ''
    }
  }
};
</script>

<style lang="less" scoped>
.breadcrumb-container {
  padding: 20px 0;
  position: relative;
  text-align: left;
  color: #ffffff;
  font-size: 16px;
  
  .header-bg {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;
    opacity: 0.8;
    filter: brightness(0.8);
    z-index: 1;
  }
  
  .container {
    position: relative;
    z-index: 2;
    max-width: 1520px;
    margin: 0 auto;
    padding: 0;
  }

  .separator {
    margin: 0 8px;
    color: rgba(255, 255, 255, 0.7);
  }

  .current {
    color: #ffffff;

  }
  
  a {
    color: #ffffff;
    text-decoration: none;
    &:hover {
      text-decoration: underline;
    }
  }
}
</style> 